import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { scrollToTop, scrollToSection } from '../utils/scrollHandlers';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import FloatingLanguageSwitcher from './FloatingLanguageSwitcher';

const CustomNavbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, logout } = useAuth();
  const { getCartCount } = useCart();
  const { language } = useLanguage();
  
  const isHomePage = location.pathname === '/';
  const cartCount = getCartCount();
  const isEdge = /Edge/.test(navigator.userAgent) || /Edg/.test(navigator.userAgent);
  
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  const isAppCardsActive = () => {
    return location.pathname === '/personal-card' || location.pathname === '/business-card' || location.pathname === '/digital-wallet';
  };
  
  const isNFCCardsActive = () => {
    return location.pathname === '/nfc-cards' || location.pathname === '/custom-cards';
  };
  
  const isAboutActive = () => {
    return location.pathname === '/about' || location.pathname === '/contact';
  };
  
  const handleLinkClick = (e, href) => {
    e.preventDefault();
    
    setMenuOpen(false);
    setActiveSubmenu(null);
    setShowUserMenu(false);
    
    if (href.startsWith('#')) {
      scrollToSection(href);
    } else if (href.startsWith('/#')) {
      if (isHomePage) {
        scrollToSection(href.substring(1));
      } else {
        navigate('/');
        setTimeout(() => {
          scrollToSection(href.substring(1));
        }, 300);
      }
    } else {
      if (location.pathname === href) {
        scrollToTop();
      } else {
        navigate(href);
        setTimeout(() => {
          scrollToTop();
        }, 100);
      }
    }
  };

  const toggleSubmenu = (submenuName) => {
    setActiveSubmenu(activeSubmenu === submenuName ? null : submenuName);
  };

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    setMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        scrollToSection(location.hash);
      }, 300);
    }
  }, [location]);

  useEffect(() => {
    setMenuOpen(false);
    setActiveSubmenu(null);
    setShowUserMenu(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.menu-toggle')) {
        setMenuOpen(false);
        setActiveSubmenu(null);
      }
      if (showUserMenu && !event.target.closest('.user-menu') && !event.target.closest('.user-menu-toggle')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen, showUserMenu]);

  return (
    <>
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-2' 
          : 'bg-white/80 backdrop-blur-sm py-3'
      } ${isEdge ? 'edge-browser' : ''}`}>
        <div className="container mx-auto px-2 sm:px-4 lg:px-3 xl:px-6">
          <div className="flex items-center justify-between w-full">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-2 sm:gap-3 font-display font-extrabold text-body sm:text-h5 lg:text-body xl:text-h4 text-dark min-w-0 flex-shrink-0"
              onClick={(e) => handleLinkClick(e, '/')}
            >
              <img src="/assets/logo.png" alt="AM CARDMATE" className="w-8 h-8 sm:w-10 sm:h-10 lg:w-10 lg:h-10 xl:w-12 xl:h-12 rounded-md object-contain" />
              <span className="text-gradient-blue-pink">CARDMATE</span>
            </Link>

            {/* Desktop Navigation */}
            <ul className="hidden lg:flex items-center gap-4 xl:gap-6 2xl:gap-8 flex-grow justify-center">
              {/* Home */}
              <li className="relative group">
                <Link 
                  to="/" 
                  className={`nav-link font-display font-semibold text-body-sm xl:text-body flex items-center gap-1 ${
                    isActive('/') ? 'text-primary-blue after:w-full' : 'text-dark'
                  } hover:text-primary-blue relative after:absolute after:bottom-[-2px] after:left-0 after:h-0.5 after:w-0 after:bg-gradient-blue after:transition-all after:duration-300 hover:after:w-full`}
                  onClick={(e) => handleLinkClick(e, '/')}
                >
                  {t('nav.home', language)}
                  {isHomePage && <FontAwesomeIcon icon="chevron-down" className="text-xs" />}
                </Link>
                
                {isHomePage && (
                  <div className="absolute left-0 mt-2 py-3 w-56 bg-white rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 border border-gray-100">
                    <a 
                      href="#features" 
                      className="block px-5 py-3 text-body text-dark hover:text-primary-blue hover:bg-blue-50 transition-all duration-200 hover:translate-x-1"
                      onClick={(e) => handleLinkClick(e, '#features')}
                    >
                      <FontAwesomeIcon icon="star" className="mr-3 text-blue-500" />
                      {t('nav.features', language)}
                    </a>
                    <a 
                      href="#how-it-works" 
                      className="block px-5 py-3 text-body text-dark hover:text-primary-blue hover:bg-blue-50 transition-all duration-200 hover:translate-x-1"
                      onClick={(e) => handleLinkClick(e, '#how-it-works')}
                    >
                      <FontAwesomeIcon icon="cogs" className="mr-3 text-blue-500" />
                      {t('nav.howItWorks', language)}
                    </a>
                    <a 
                      href="#nfc-cards" 
                      className="block px-5 py-3 text-body text-dark hover:text-primary-blue hover:bg-blue-50 transition-all duration-200 hover:translate-x-1"
                      onClick={(e) => handleLinkClick(e, '#nfc-cards')}
                    >
                      <FontAwesomeIcon icon="credit-card" className="mr-3 text-blue-500" />
                      {t('nav.nfcCards', language)}
                    </a>
                    <a 
                      href="#testimonials" 
                      className="block px-5 py-3 text-body text-dark hover:text-primary-blue hover:bg-blue-50 transition-all duration-200 hover:translate-x-1"
                      onClick={(e) => handleLinkClick(e, '#testimonials')}
                    >
                      <FontAwesomeIcon icon="heart" className="mr-3 text-blue-500" />
                      {t('nav.reviews', language)}
                    </a>
                  </div>
                )}
              </li>
              
              {/* App Cards */}
              <li className="relative group">
                <span 
                  className={`font-display font-semibold text-body-sm xl:text-body cursor-pointer flex items-center gap-1 ${
                    isAppCardsActive() ? 'text-primary-blue after:w-full' : 'text-dark'
                  } hover:text-primary-blue relative after:absolute after:bottom-[-2px] after:left-0 after:h-0.5 after:w-0 after:bg-gradient-blue after:transition-all after:duration-300 hover:after:w-full`}
                >
                  <span className="lg:hidden xl:inline">{t('nav.appCards', language)}</span>
                  <span className="hidden lg:inline xl:hidden">{t('nav.cards', language)}</span>
                  <FontAwesomeIcon icon="chevron-down" className="text-xs" />
                </span>
                
                <div className="absolute left-0 mt-2 py-3 w-56 bg-white rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 border border-gray-100">
                  <Link 
                    to="/personal-card" 
                    className="block px-5 py-3 text-body text-dark hover:text-primary-blue hover:bg-blue-50 transition-all duration-200 hover:translate-x-1"
                    onClick={(e) => handleLinkClick(e, '/personal-card')}
                  >
                    <FontAwesomeIcon icon="user" className="mr-3 text-blue-500" />
                    {t('nav.personalCard', language)}
                  </Link>
                  <Link 
                    to="/business-card" 
                    className="block px-5 py-3 text-body text-dark hover:text-primary-blue hover:bg-blue-50 transition-all duration-200 hover:translate-x-1"
                    onClick={(e) => handleLinkClick(e, '/business-card')}
                  >
                    <FontAwesomeIcon icon="briefcase" className="mr-3 text-blue-500" />
                    {t('nav.businessCard', language)}
                  </Link>
                  <Link 
                    to="/digital-wallet" 
                    className="block px-5 py-3 text-body text-dark hover:text-primary-blue hover:bg-blue-50 transition-all duration-200 hover:translate-x-1"
                    onClick={(e) => handleLinkClick(e, '/digital-wallet')}
                  >
                    <FontAwesomeIcon icon="wallet" className="mr-3 text-blue-500" />
                    Digital Wallet
                    <span className="ml-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-caption px-2 py-0.5 rounded-full font-medium">
                      New
                    </span>
                  </Link>
                </div>
              </li>
              
              {/* Enterprise */}
              <li className="relative">
                <Link 
                  to="/enterprise" 
                  className={`font-display font-semibold text-body-sm xl:text-body ${
                    isActive('/enterprise') ? 'text-primary-blue after:w-full' : 'text-dark'
                  } hover:text-primary-blue relative after:absolute after:bottom-[-2px] after:left-0 after:h-0.5 after:w-0 after:bg-gradient-blue after:transition-all after:duration-300 hover:after:w-full flex items-center gap-2`}
                  onClick={(e) => handleLinkClick(e, '/enterprise')}
                >
                  {t('nav.enterprise', language)}
                  <span className="bg-primary-blue text-white text-caption px-2 py-0.5 rounded-full font-medium">
                    {t('nav.soon', language)}
                  </span>
                </Link>
              </li>
              
              {/* NFC Cards */}
              <li className="relative group">
                <Link 
                  to="/nfc-cards" 
                  className={`font-display font-semibold text-body-sm xl:text-body flex items-center gap-1 ${
                    isNFCCardsActive() ? 'text-primary-blue after:w-full' : 'text-dark'
                  } hover:text-primary-blue relative after:absolute after:bottom-[-2px] after:left-0 after:h-0.5 after:w-0 after:bg-gradient-blue after:transition-all after:duration-300 hover:after:w-full`}
                  onClick={(e) => handleLinkClick(e, '/nfc-cards')}
                >
                  <span className="lg:hidden xl:inline">{t('nav.nfcCards', language)}</span>
                  <span className="hidden lg:inline xl:hidden">{t('nav.nfc', language)}</span>
                  <FontAwesomeIcon icon="chevron-down" className="text-xs" />
                </Link>
                
                <div className="absolute left-0 mt-2 py-3 w-64 bg-white rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 border border-gray-100">
                  <Link 
                    to="/nfc-cards" 
                    className="block px-5 py-3 text-body text-dark hover:text-primary-blue hover:bg-blue-50 transition-all duration-200 hover:translate-x-1"
                    onClick={(e) => handleLinkClick(e, '/nfc-cards')}
                  >
                    <FontAwesomeIcon icon="mobile-alt" className="mr-3 text-blue-500" />
                    {t('nav.appNfcCards', language)}
                  </Link>
                  <Link 
                    to="/custom-cards" 
                    className="block px-5 py-3 text-body text-dark hover:text-primary-blue hover:bg-blue-50 transition-all duration-200 hover:translate-x-1"
                    onClick={(e) => handleLinkClick(e, '/custom-cards')}
                  >
                    <FontAwesomeIcon icon="palette" className="mr-3 text-blue-500" />
                    {t('nav.customCards', language)}
                  </Link>
                </div>
              </li>
              
              {/* About */}
              <li className="relative group">
                <span 
                  className={`font-display font-semibold text-body-sm xl:text-body cursor-pointer flex items-center gap-1 ${
                    isAboutActive() ? 'text-primary-blue after:w-full' : 'text-dark'
                  } hover:text-primary-blue relative after:absolute after:bottom-[-2px] after:left-0 after:h-0.5 after:w-0 after:bg-gradient-blue after:transition-all after:duration-300 hover:after:w-full`}
                >
                  {t('nav.about', language)}
                  <FontAwesomeIcon icon="chevron-down" className="text-xs" />
                </span>
                
                <div className="absolute left-0 mt-2 py-3 w-56 bg-white rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 border border-gray-100">
                  <Link 
                    to="/about" 
                    className="block px-5 py-3 text-body text-dark hover:text-primary-blue hover:bg-blue-50 transition-all duration-200 hover:translate-x-1"
                    onClick={(e) => handleLinkClick(e, '/about')}
                  >
                    <FontAwesomeIcon icon="info-circle" className="mr-3 text-blue-500" />
                    {t('nav.about', language)}
                  </Link>
                  <Link 
                    to="/contact" 
                    className="block px-5 py-3 text-body text-dark hover:text-primary-blue hover:bg-blue-50 transition-all duration-200 hover:translate-x-1"
                    onClick={(e) => handleLinkClick(e, '/contact')}
                  >
                    <FontAwesomeIcon icon="envelope" className="mr-3 text-blue-500" />
                    {t('nav.contact', language)}
                  </Link>
                </div>
              </li>
              
              {/* Blog */}
              <li className="relative">
                <Link 
                  to="/blog" 
                  className={`font-display font-semibold text-body-sm xl:text-body ${
                    isActive('/blog') || location.pathname.startsWith('/blog/') ? 'text-primary-blue after:w-full' : 'text-dark'
                  } hover:text-primary-blue relative after:absolute after:bottom-[-2px] after:left-0 after:h-0.5 after:w-0 after:bg-gradient-blue after:transition-all after:duration-300 hover:after:w-full`}
                  onClick={(e) => handleLinkClick(e, '/blog')}
                >
                  {t('nav.blog', language)}
                </Link>
              </li>
            </ul>

            {/* Desktop Right Section */}
            <div className="hidden lg:flex items-center gap-1 xl:gap-2 2xl:gap-3 flex-shrink-0">
              {/* Cart */}
              <Link 
                to="/cart" 
                className="relative p-2 text-dark hover:text-primary-blue transition-colors"
                onClick={(e) => handleLinkClick(e, '/cart')}
              >
                <FontAwesomeIcon icon="shopping-cart" className="text-lg xl:text-xl" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-caption rounded-full h-5 w-5 flex items-center justify-center font-medium">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Link>

              {/* User Menu */}
              {user ? (
                <div className="relative user-menu">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 p-2 text-dark hover:text-primary-blue transition-colors user-menu-toggle"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-body-sm font-bold shadow-lg">
                      {userProfile?.firstName?.charAt(0) || user.email?.charAt(0) || 'U'}
                    </div>
                    <span className="hidden 2xl:inline font-display font-medium text-body-sm">
                      {userProfile?.firstName || 'User'}
                    </span>
                    <FontAwesomeIcon icon="chevron-down" className="text-xs hidden 2xl:inline" />
                  </button>
                  
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 py-3 w-56 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl z-50">
                      <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 mx-2 rounded-xl mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                            {userProfile?.firstName?.charAt(0) || user.email?.charAt(0) || 'U'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-body-sm font-semibold text-dark truncate">
                              {userProfile?.firstName} {userProfile?.lastName}
                            </p>
                            <p className="text-caption text-gray-600 truncate">{user.email}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="px-2">
                        <Link 
                          to="/account" 
                          className="flex items-center gap-3 px-3 py-2 text-dark hover:text-primary-blue hover:bg-blue-50 transition-colors rounded-xl"
                          onClick={(e) => handleLinkClick(e, '/account')}
                        >
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <FontAwesomeIcon icon="user" className="text-blue-600 text-body-sm" />
                          </div>
                          <span className="text-body-sm font-medium">{t('nav.myAccount', language)}</span>
                        </Link>
                        
                        <div className="h-px bg-gray-200 my-2 mx-3"></div>
                        
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 transition-colors rounded-xl"
                        >
                          <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                            <FontAwesomeIcon icon="sign-out-alt" className="text-red-600 text-body-sm" />
                          </div>
                          <span className="text-body-sm font-medium">{t('nav.logout', language)}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="font-display font-semibold text-dark hover:text-primary-blue transition-colors px-2 xl:px-3 py-2 text-body-sm xl:text-body"
                    onClick={(e) => handleLinkClick(e, '/login')}
                  >
                    {t('nav.login', language)}
                  </Link>
                  
                  <Link 
                    to="/signup" 
                    className="btn btn-primary btn-sm"
                    onClick={(e) => handleLinkClick(e, '/signup')}
                  >
                    <FontAwesomeIcon icon="user-plus" />
                    <span className="hidden lg:inline xl:hidden">{t('nav.sign', language)}</span>
                    <span className="hidden xl:inline">{t('nav.signUp', language)}</span>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="lg:hidden flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-lg sm:text-xl text-dark focus:outline-none menu-toggle"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <FontAwesomeIcon icon={menuOpen ? "times" : "bars"} />
            </button>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <div className="lg:hidden mobile-menu bg-white border-t border-gray-200 mt-2 py-3 sm:py-4 rounded-b-2xl shadow-lg">
              <div className="px-2 sm:px-4 space-y-2 sm:space-y-3">
                <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                  <span className="font-display font-semibold text-dark text-body">Menu</span>
                </div>
                
                {/* Home */}
                <div>
                  <Link 
                    to="/" 
                    className={`block font-display font-semibold text-body py-2 ${
                      isActive('/') ? 'text-primary-blue' : 'text-dark'
                    }`}
                    onClick={(e) => handleLinkClick(e, '/')}
                  >
                    {t('nav.home', language)}
                  </Link>
                  
                  {isHomePage && (
                    <div className="ml-4 mt-2 space-y-1 sm:space-y-2">
                      <a 
                        href="#features" 
                        className="flex items-center gap-3 text-body-sm text-gray-600 py-1 hover:text-primary-blue transition-colors"
                        onClick={(e) => handleLinkClick(e, '#features')}
                      >
                        <FontAwesomeIcon icon="star" className="text-blue-500" />
                        {t('nav.features', language)}
                      </a>
                      <a 
                        href="#how-it-works" 
                        className="flex items-center gap-3 text-body-sm text-gray-600 py-1 hover:text-primary-blue transition-colors"
                        onClick={(e) => handleLinkClick(e, '#how-it-works')}
                      >
                        <FontAwesomeIcon icon="cogs" className="text-blue-500" />
                        {t('nav.howItWorks', language)}
                      </a>
                      <a 
                        href="#nfc-cards" 
                        className="flex items-center gap-3 text-body-sm text-gray-600 py-1 hover:text-primary-blue transition-colors"
                        onClick={(e) => handleLinkClick(e, '#nfc-cards')}
                      >
                        <FontAwesomeIcon icon="credit-card" className="text-blue-500" />
                        {t('nav.nfcCards', language)}
                      </a>
                      <a 
                        href="#testimonials" 
                        className="flex items-center gap-3 text-body-sm text-gray-600 py-1 hover:text-primary-blue transition-colors"
                        onClick={(e) => handleLinkClick(e, '#testimonials')}
                      >
                        <FontAwesomeIcon icon="heart" className="text-blue-500" />
                        {t('nav.reviews', language)}
                      </a>
                    </div>
                  )}
                </div>

                {/* App Cards */}
                <div>
                  <button 
                    className={`flex items-center justify-between w-full font-display font-semibold text-body py-2 ${
                      isAppCardsActive() ? 'text-primary-blue' : 'text-dark'
                    }`}
                    onClick={() => toggleSubmenu('app-cards')}
                  >
                    <span>{t('nav.appCards', language)}</span>
                    <FontAwesomeIcon 
                      icon="chevron-down" 
                      className={`text-body-sm transition-transform duration-200 ${activeSubmenu === 'app-cards' ? 'rotate-180' : ''}`}
                    />
                  </button>
                  
                  {activeSubmenu === 'app-cards' && (
                    <div className="ml-4 mt-2 space-y-1 sm:space-y-2">
                      <Link 
                        to="/personal-card" 
                        className="flex items-center gap-3 text-body-sm text-gray-600 py-1 hover:text-primary-blue transition-colors"
                        onClick={(e) => handleLinkClick(e, '/personal-card')}
                      >
                        <FontAwesomeIcon icon="user" className="text-blue-500" />
                        {t('nav.personalCard', language)}
                      </Link>
                      <Link 
                        to="/business-card" 
                        className="flex items-center gap-3 text-body-sm text-gray-600 py-1 hover:text-primary-blue transition-colors"
                        onClick={(e) => handleLinkClick(e, '/business-card')}
                      >
                        <FontAwesomeIcon icon="briefcase" className="text-blue-500" />
                        {t('nav.businessCard', language)}
                      </Link>
                      <Link 
                        to="/digital-wallet" 
                        className="flex items-center gap-3 text-body-sm text-gray-600 py-1 hover:text-primary-blue transition-colors"
                        onClick={(e) => handleLinkClick(e, '/digital-wallet')}
                      >
                        <FontAwesomeIcon icon="wallet" className="text-blue-500" />
                        Digital Wallet
                        <span className="ml-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-caption px-2 py-0.5 rounded-full font-medium">
                          New
                        </span>
                      </Link>
                    </div>
                  )}
                </div>

                {/* Enterprise */}
                <Link 
                  to="/enterprise" 
                  className={`flex items-center gap-2 font-display font-semibold text-body py-2 ${
                    isActive('/enterprise') ? 'text-primary-blue' : 'text-dark'
                  }`}
                  onClick={(e) => handleLinkClick(e, '/enterprise')}
                >
                  {t('nav.enterprise', language)}
                  <span className="bg-primary-blue text-white text-caption px-2 py-0.5 rounded-full font-medium">
                    {t('nav.soon', language)}
                  </span>
                </Link>

                {/* NFC Cards */}
                <div>
                  <button 
                    className={`flex items-center justify-between w-full font-display font-semibold text-body py-2 ${
                      isNFCCardsActive() ? 'text-primary-blue' : 'text-dark'
                    }`}
                    onClick={() => toggleSubmenu('nfc-cards')}
                  >
                    <span>{t('nav.nfcCards', language)}</span>
                    <FontAwesomeIcon 
                      icon="chevron-down" 
                      className={`text-body-sm transition-transform duration-200 ${activeSubmenu === 'nfc-cards' ? 'rotate-180' : ''}`}
                    />
                  </button>
                  
                  {activeSubmenu === 'nfc-cards' && (
                    <div className="ml-4 mt-2 space-y-1 sm:space-y-2">
                      <Link 
                        to="/nfc-cards" 
                        className="flex items-center gap-3 text-body-sm text-gray-600 py-1 hover:text-primary-blue transition-colors"
                        onClick={(e) => handleLinkClick(e, '/nfc-cards')}
                      >
                        <FontAwesomeIcon icon="mobile-alt" className="text-blue-500" />
                        {t('nav.appNfcCards', language)}
                      </Link>
                      <Link 
                        to="/custom-cards" 
                        className="flex items-center gap-3 text-body-sm text-gray-600 py-1 hover:text-primary-blue transition-colors"
                        onClick={(e) => handleLinkClick(e, '/custom-cards')}
                      >
                        <FontAwesomeIcon icon="palette" className="text-blue-500" />
                        {t('nav.customCards', language)}
                      </Link>
                    </div>
                  )}
                </div>

                {/* About */}
                <div>
                  <button 
                    className={`flex items-center justify-between w-full font-display font-semibold text-body py-2 ${
                      isAboutActive() ? 'text-primary-blue' : 'text-dark'
                    }`}
                    onClick={() => toggleSubmenu('about')}
                  >
                    <span>{t('nav.about', language)}</span>
                    <FontAwesomeIcon 
                      icon="chevron-down" 
                      className={`text-body-sm transition-transform duration-200 ${activeSubmenu === 'about' ? 'rotate-180' : ''}`}
                    />
                  </button>
                  
                  {activeSubmenu === 'about' && (
                    <div className="ml-4 mt-2 space-y-1 sm:space-y-2">
                      <Link 
                        to="/about" 
                        className="flex items-center gap-3 text-body-sm text-gray-600 py-1 hover:text-primary-blue transition-colors"
                        onClick={(e) => handleLinkClick(e, '/about')}
                      >
                        <FontAwesomeIcon icon="info-circle" className="text-blue-500" />
                        {t('nav.about', language)}
                      </Link>
                      <Link 
                        to="/contact" 
                        className="flex items-center gap-3 text-body-sm text-gray-600 py-1 hover:text-primary-blue transition-colors"
                        onClick={(e) => handleLinkClick(e, '/contact')}
                      >
                        <FontAwesomeIcon icon="envelope" className="text-blue-500" />
                        {t('nav.contact', language)}
                      </Link>
                    </div>
                  )}
                </div>

                {/* Blog */}
                <Link 
                  to="/blog" 
                  className={`block font-display font-semibold text-body py-2 ${
                    isActive('/blog') || location.pathname.startsWith('/blog/') ? 'text-primary-blue' : 'text-dark'
                  }`}
                  onClick={(e) => handleLinkClick(e, '/blog')}
                >
                  {t('nav.blog', language)}
                </Link>

                {/* Mobile Footer */}
                <div className="border-t border-gray-100 pt-3 sm:pt-4 mt-3 sm:mt-4 space-y-2 sm:space-y-3">
                  {/* Cart */}
                  <Link 
                    to="/cart" 
                    className="flex items-center gap-3 py-2"
                    onClick={(e) => handleLinkClick(e, '/cart')}
                  >
                    <FontAwesomeIcon icon="shopping-cart" className="text-primary-blue" />
                    <span className="font-display font-semibold text-dark text-body">{t('nav.cart', language)}</span>
                    {cartCount > 0 && (
                      <span className="bg-red-500 text-white text-caption rounded-full h-5 w-5 flex items-center justify-center font-medium ml-auto">
                        {cartCount > 99 ? '99+' : cartCount}
                      </span>
                    )}
                  </Link>
                  
                  {/* User Section */}
                  {user ? (
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex items-center gap-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl px-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-body-sm font-bold">
                          {userProfile?.firstName?.charAt(0) || user.email?.charAt(0) || 'U'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-body-sm font-semibold text-dark truncate">
                            {userProfile?.firstName} {userProfile?.lastName}
                          </p>
                          <p className="text-caption text-gray-600 truncate">{user.email}</p>
                        </div>
                      </div>
                      
                      <Link 
                        to="/account" 
                        className="flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-blue-50 transition-colors"
                        onClick={(e) => handleLinkClick(e, '/account')}
                      >
                        <FontAwesomeIcon icon="user" className="text-blue-500" />
                        <span className="font-display font-semibold text-dark text-body">{t('nav.myAccount', language)}</span>
                      </Link>
                      
                      <button 
                        onClick={handleLogout}
                        className="flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-red-50 text-red-600 transition-colors w-full text-left"
                      >
                        <FontAwesomeIcon icon="sign-out-alt" />
                        <span className="font-display font-semibold text-body">{t('nav.logout', language)}</span>
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2 sm:gap-3">
                      <Link 
                        to="/login" 
                        className="flex-1 text-center py-2 px-4 border border-primary-blue text-primary-blue rounded-lg font-display font-semibold text-body-sm"
                        onClick={(e) => handleLinkClick(e, '/login')}
                      >
                        {t('nav.login', language)}
                      </Link>
                      <Link 
                        to="/signup" 
                        className="flex-1 text-center py-2 px-4 bg-primary-blue text-white rounded-lg font-display font-semibold text-body-sm"
                        onClick={(e) => handleLinkClick(e, '/signup')}
                      >
                        {t('nav.signUp', language)}
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
      
      <FloatingLanguageSwitcher />
      
      <div className={`${scrolled ? 'h-14' : 'h-16'}`} />
    </>
  );
};

export default CustomNavbar;